/* eslint-disable max-len */
import OgImage from '@propertypal/shared/src/resources/images/og-image-sitewide.jpeg';
import { GREY_300 } from '@propertypal/shared/src/styles/theme';
import { MetaDescriptions } from '@propertypal/shared/src/types';
import { getLocationHeader } from '@propertypal/shared/src/utils/getUserAgent';
import React, { Fragment, FunctionComponent } from 'react';

interface TwitterData {
  key: string;
  value: string;
}

interface LatLng {
  latitude?: number;
  longitude?: number;
}

interface Props {
  title?: string;
  description?: string;
  url?: string;
  nextUrl?: string;
  prevUrl?: string;
  shortlink?: string;
  image?: {
    url: string;
    width: string;
    height: string;
  };
  twitterData?: TwitterData[];
  type?: string;
  updatedTime?: string;
  latlng?: LatLng;
  accountNumber?: string;
  metaDescriptions?: MetaDescriptions;
}

export const BASE_URL = 'https://www.propertypal.com';
export const DEFAULT_TITLE = 'PropertyPal - Find Property For Sale & Rent In Ireland & Northern Ireland';
export const DEFAULT_DESCRIPTION =
  'Browse Through More Than 60,000 Properties For Sale And Rent In Ireland & Northern Ireland. ' +
  'PropertyPal Is Your Best Friend In Property.';
export const DEFAULT_TYPE = 'website';

const DEFAULT_IMAGE = {
  url: `${BASE_URL}${OgImage.src}`,
  width: '1200',
  height: '630',
};

const MetaTags: FunctionComponent<Props> = (props) => {
  const url = props.url ? `${BASE_URL}${props.url}` : BASE_URL;
  const title = props.title || props.metaDescriptions?.title || DEFAULT_TITLE;
  const description = props.description || props.metaDescriptions?.description || DEFAULT_DESCRIPTION;
  const image = props.image || DEFAULT_IMAGE;
  const usr = getLocationHeader === 'IE' ? 'IRL' : 'GBR';
  const type = props.type || DEFAULT_TYPE;

  return (
    <>
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

      {!!props.shortlink && <link rel="shortlink" href={props.shortlink} />}
      <link key="link:canonical" rel="canonical" href={url} />
      {!!props.nextUrl && <link key="link:next" rel="next" href={`${BASE_URL}${props.nextUrl}`} />}
      {!!props.prevUrl && <link key="link:prev" rel="prev" href={`${BASE_URL}${props.prevUrl}`} />}

      <meta name="description" content={description} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta property="fb:app_id" content="120078844683782" />
      <meta name="referrer" content="always" />
      <meta name="usr" content={usr} />
      <meta name="theme-color" content={GREY_300} />
      <meta name="robots" content="noodp" />
      {/* Bing Webmaster Center verification */}
      <meta name="msvalidate.01" content="FF4B57E0082F5B54429F27FFB6C49CB2" />
      {/* Jedi Mind Trick */}
      <meta itemProp="jedi-mind-trick" content="You will bookmark this site and use it a lot." />
      <meta name="format-detection" content="telephone=no" />
      {props.accountNumber && <meta itemProp="pp-gaListingAccountNumber" content={props.accountNumber} />}
      {props.latlng && (
        <>
          {props.latlng.latitude && (
            <meta property="place:location:latitude" content={props.latlng.latitude.toString()} />
          )}
          {props.latlng.longitude && (
            <meta property="place:location:longitude" content={props.latlng.longitude.toString()} />
          )}
        </>
      )}

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@propertypal" />
      <meta name="twitter:creator" content="@propertypal" />
      <meta name="twitter:domain" content="propertypal.com" />
      <meta name="twitter:image" content={image.url} />
      {/* Twitter Extra Data */}
      {props.metaDescriptions?.twitterExtraData &&
        Object.keys(props.metaDescriptions.twitterExtraData).map((d, i) => (
          <Fragment key={d}>
            <meta property={`twitter:label${i + 1}`} content={d} />
            <meta property={`twitter:data${i + 1}`} content={props.metaDescriptions?.twitterExtraData[d]} />
          </Fragment>
        ))}

      <meta property="og:title" content={title} />
      <meta property="og:url" content={url} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="PropertyPal.com" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image:user_generated" content="false" />
      <meta property="og:image" content={image.url} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content={image.width} />
      <meta property="og:image:height" content={image.height} />
      {props.updatedTime && <meta property="og:updated_time" content={props.updatedTime} />}
    </>
  );
};

export default MetaTags;
